/**
 * T-032 — Admin console API contracts (tasks/W3-CONTRACTS.md §9 · docs/10-design/14 §1)
 * self-contained (ไม่ import จาก ./index.ts) — กัน circular TDZ (แบบเดียวกับ workflow.ts/webhooks.ts)
 */
import { z } from "zod";

const I18n = z.record(z.string(), z.string());
const CODE_RE = /^[A-Z0-9]{2,4}$/;
const SLUG_RE = /^[a-z0-9_]+$/;

// ---------------------------------------------------------------------------
// Services + email routes
// ---------------------------------------------------------------------------

export const AdminService = z.object({
  id: z.uuid(),
  code: z.string(),
  name: I18n,
  description: I18n.nullable(),
  department_id: z.uuid().nullable(),
  intake_team_id: z.uuid().nullable(),
  status_resolution: z.enum(["by_holder_team", "by_service"]),
  default_status_set_id: z.uuid().nullable(),
  priority_scheme_id: z.uuid().nullable(),
  default_priority_id: z.uuid().nullable(),
  default_language: z.string(),
  csat_mode: z.enum(["off", "on_close", "in_every_reply_footer"]),
  csat_scale: z.string(),
  csat_delay_hours: z.number().int(),
  lawful_basis: z.string().nullable(),
  privacy_notice: I18n.nullable(),
  is_internal: z.boolean(),
  /** [T-063 R6] ค่าเริ่มต้นการส่งอีเมลของ public reply ทีมงาน (true = ส่งเหมือนเดิม · false = ส่งเฉพาะเมื่อผู้ส่งระบุ notify_by_email=true) */
  default_notify_by_email: z.boolean(),
  status: z.enum(["active", "archived"]),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type AdminService = z.infer<typeof AdminService>;

export const AdminServiceInput = z.strictObject({
  code: z.string().regex(CODE_RE),
  name: I18n,
  description: I18n.optional(),
  department_id: z.uuid().nullable().optional(),
  intake_team_id: z.uuid().nullable().optional(),
  status_resolution: z.enum(["by_holder_team", "by_service"]).optional(),
  default_status_set_id: z.uuid().nullable().optional(),
  priority_scheme_id: z.uuid().nullable().optional(),
  default_priority_id: z.uuid().nullable().optional(),
  default_language: z.string().min(2).max(8).optional(),
  csat_mode: z.enum(["off", "on_close", "in_every_reply_footer"]).optional(),
  csat_scale: z.string().max(16).optional(),
  csat_delay_hours: z.number().int().min(0).max(720).optional(),
  lawful_basis: z.string().max(64).nullable().optional(),
  privacy_notice: I18n.nullable().optional(),
  is_internal: z.boolean().optional(),
  default_notify_by_email: z.boolean().optional(),
});
export type AdminServiceInput = z.infer<typeof AdminServiceInput>;
export const AdminServicePatch = AdminServiceInput.partial().extend({ status: z.enum(["active", "archived"]).optional() });
export type AdminServicePatch = z.infer<typeof AdminServicePatch>;

export const EmailRoute = z.object({
  id: z.uuid(),
  pattern: z.string(),
  kind: z.enum(["mailbox", "mail_account", "system", "reject"]),
  target_id: z.uuid().nullable(),
  system_handler: z.string().nullable(),
  priority: z.number().int(),
  enabled: z.boolean(),
  created_at: z.iso.datetime(),
});
export type EmailRoute = z.infer<typeof EmailRoute>;

export const EmailRouteInput = z.strictObject({
  pattern: z.string().trim().min(1).max(320),
  kind: z.enum(["mailbox", "mail_account", "system", "reject"]),
  target_id: z.uuid().nullable().optional(),
  system_handler: z.string().max(64).nullable().optional(),
  priority: z.number().int().optional(),
  enabled: z.boolean().optional(),
});
export type EmailRouteInput = z.infer<typeof EmailRouteInput>;

// ---------------------------------------------------------------------------
// Mailboxes
// ---------------------------------------------------------------------------

export const MailboxAlias = z.object({ address: z.email(), name: z.string().max(120).optional() });
export const ReplyPolicy = z.object({
  status_after_reply: z.string().optional(),
  assign_on_reply: z.enum(["keep", "replier", "replier_if_unassigned", "unassign"]).optional(),
});

export const AdminMailbox = z.object({
  id: z.uuid(),
  address: z.string(),
  display_name: z.string(),
  aliases: z.array(MailboxAlias),
  allow_alias_reply: z.boolean(),
  service_id: z.uuid(),
  intake_team_id: z.uuid().nullable(),
  language: z.string(),
  from_name_mode: z.enum(["mailbox", "agent", "template"]),
  from_name_template: z.string().nullable(),
  reply_policy: ReplyPolicy,
  signature_id: z.uuid().nullable(),
  history_mode: z.enum(["none", "last", "full"]),
  subject_format: z.string(),
  auto_bcc: z.array(z.string()),
  auto_cc_from_inbound: z.boolean(),
  accept_cc_participants: z.boolean(),
  routing_rules: z.array(z.unknown()),
  use_ai_routing: z.boolean(),
  ai_threshold: z.string(),
  spam_settings: z.record(z.string(), z.unknown()).nullable(),
  block_external_images_default: z.boolean(),
  email_template_id: z.uuid().nullable(),
  sending_identity_id: z.uuid().nullable(),
  icon: z.object({ emoji: z.string().optional(), color: z.string().optional(), asset: z.string().optional() }).nullable(),
  status: z.enum(["active", "archived"]),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type AdminMailbox = z.infer<typeof AdminMailbox>;

export const AdminMailboxInput = z.strictObject({
  address: z.email(),
  display_name: z.string().trim().min(1).max(160),
  aliases: z.array(MailboxAlias).max(20).optional(),
  allow_alias_reply: z.boolean().optional(),
  service_id: z.uuid(),
  intake_team_id: z.uuid().nullable().optional(),
  language: z.string().min(2).max(8).optional(),
  from_name_mode: z.enum(["mailbox", "agent", "template"]).optional(),
  from_name_template: z.string().max(200).nullable().optional(),
  reply_policy: ReplyPolicy.optional(),
  signature_id: z.uuid().nullable().optional(),
  history_mode: z.enum(["none", "last", "full"]).optional(),
  subject_format: z.string().max(200).optional(),
  auto_bcc: z.array(z.email()).max(20).optional(),
  auto_cc_from_inbound: z.boolean().optional(),
  accept_cc_participants: z.boolean().optional(),
  use_ai_routing: z.boolean().optional(),
  ai_threshold: z.number().min(0).max(1).optional(),
  block_external_images_default: z.boolean().optional(),
  sending_identity_id: z.uuid().nullable().optional(),
  icon: z.object({ emoji: z.string().optional(), color: z.string().optional(), asset: z.string().optional() }).nullable().optional(),
});
export type AdminMailboxInput = z.infer<typeof AdminMailboxInput>;
export const AdminMailboxPatch = AdminMailboxInput.partial().extend({ status: z.enum(["active", "archived"]).optional() });
export type AdminMailboxPatch = z.infer<typeof AdminMailboxPatch>;

// ---------------------------------------------------------------------------
// Departments / teams / members
// ---------------------------------------------------------------------------

export const AdminDepartment = z.object({
  id: z.uuid(),
  code: z.string(),
  name: I18n,
  description: I18n.nullable(),
  intake_team_id: z.uuid().nullable(),
  business_hours_id: z.uuid().nullable(),
  status: z.enum(["active", "archived"]),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type AdminDepartment = z.infer<typeof AdminDepartment>;

export const AdminDepartmentInput = z.strictObject({
  code: z.string().trim().min(1).max(32).regex(SLUG_RE),
  name: I18n,
  description: I18n.optional(),
  intake_team_id: z.uuid().nullable().optional(),
  business_hours_id: z.uuid().nullable().optional(),
});
export type AdminDepartmentInput = z.infer<typeof AdminDepartmentInput>;
export const AdminDepartmentPatch = AdminDepartmentInput.partial().extend({ status: z.enum(["active", "archived"]).optional() });
export type AdminDepartmentPatch = z.infer<typeof AdminDepartmentPatch>;

export const AdminTeam = z.object({
  id: z.uuid(),
  department_id: z.uuid().nullable(),
  code: z.string(),
  name: I18n,
  icon: z.string().nullable(),
  color: z.string().nullable(),
  assignment_mode: z.enum(["claim", "round_robin", "load_balanced"]),
  assign_only_in_business_hours: z.boolean(),
  default_status_set_id: z.uuid().nullable(),
  default_signature_id: z.uuid().nullable(),
  business_hours_id: z.uuid().nullable(),
  wip_limits: z.record(z.string(), z.number()).nullable(),
  notification_defaults: z.record(z.string(), z.unknown()).nullable(),
  status: z.enum(["active", "archived"]),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type AdminTeam = z.infer<typeof AdminTeam>;

export const AdminTeamInput = z.strictObject({
  department_id: z.uuid().nullable().optional(),
  code: z.string().trim().min(1).max(32).regex(SLUG_RE),
  name: I18n,
  icon: z.string().max(64).nullable().optional(),
  color: z.string().max(32).nullable().optional(),
  assignment_mode: z.enum(["claim", "round_robin", "load_balanced"]).optional(),
  assign_only_in_business_hours: z.boolean().optional(),
  default_status_set_id: z.uuid().nullable().optional(),
  default_signature_id: z.uuid().nullable().optional(),
  business_hours_id: z.uuid().nullable().optional(),
  wip_limits: z.record(z.string(), z.number().int().positive()).nullable().optional(),
  notification_defaults: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type AdminTeamInput = z.infer<typeof AdminTeamInput>;
export const AdminTeamPatch = AdminTeamInput.partial().extend({ status: z.enum(["active", "archived"]).optional() });
export type AdminTeamPatch = z.infer<typeof AdminTeamPatch>;

export const AdminTeamMember = z.object({
  team_id: z.uuid(),
  user_id: z.uuid(),
  role: z.enum(["team_lead", "member", "viewer"]),
  rr_weight: z.number().int(),
  rr_last_assigned_at: z.iso.datetime().nullable(),
  max_open_tickets: z.number().int().nullable(),
  out_of_office_until: z.iso.datetime().nullable(),
  joined_at: z.iso.datetime(),
  user: z.object({ id: z.uuid(), display_name: z.string().nullable(), primary_email: z.string(), avatar_url: z.string().nullable() }),
});
export type AdminTeamMember = z.infer<typeof AdminTeamMember>;

export const AdminTeamMemberInput = z.strictObject({
  user_id: z.uuid(),
  role: z.enum(["team_lead", "member", "viewer"]).optional(),
});
export type AdminTeamMemberInput = z.infer<typeof AdminTeamMemberInput>;

export const AdminTeamMemberPatch = z.strictObject({
  role: z.enum(["team_lead", "member", "viewer"]).optional(),
  rr_weight: z.number().int().positive().optional(),
  max_open_tickets: z.number().int().positive().nullable().optional(),
  out_of_office_until: z.iso.datetime().nullable().optional(),
});
export type AdminTeamMemberPatch = z.infer<typeof AdminTeamMemberPatch>;

// ---------------------------------------------------------------------------
// Users + sessions
// ---------------------------------------------------------------------------

export const AdminUser = z.object({
  id: z.uuid(),
  sso_sub: z.uuid().nullable(),
  primary_email: z.string(),
  display_name: z.string().nullable(),
  kind: z.enum(["staff", "customer", "guest", "service"]),
  is_admin: z.boolean(),
  status: z.enum(["active", "suspended", "deleted_at_idp", "anonymized"]),
  locale: z.string(),
  timezone: z.string(),
  created_at: z.iso.datetime(),
  last_seen_at: z.iso.datetime().nullable(),
});
export type AdminUser = z.infer<typeof AdminUser>;

export const AdminUserPatch = z.strictObject({
  display_name: z.string().trim().min(1).max(80).optional(),
  kind: z.enum(["staff", "customer", "guest", "service"]).optional(),
  is_admin: z.boolean().optional(),
  status: z.enum(["active", "suspended"]).optional(),
  locale: z.enum(["th", "en"]).optional(),
});
export type AdminUserPatch = z.infer<typeof AdminUserPatch>;

export const AdminUserListQuery = z.object({
  q: z.string().trim().max(200).optional(),
  kind: z.enum(["staff", "customer", "guest", "service"]).optional(),
  status: z.enum(["active", "suspended", "deleted_at_idp", "anonymized"]).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type AdminUserListQuery = z.infer<typeof AdminUserListQuery>;

export const AdminUserList = z.object({ items: z.array(AdminUser), next_cursor: z.string().nullable() });
export type AdminUserList = z.infer<typeof AdminUserList>;

export const AdminSession = z.object({
  id: z.string(),
  kind: z.enum(["sso", "guest_link", "api", "native_app"]),
  ip: z.string().nullable(),
  ua_hash: z.string().nullable(),
  device_label: z.string().nullable(),
  issued_at: z.iso.datetime(),
  last_seen_at: z.iso.datetime(),
  expires_at: z.iso.datetime(),
  revoked_at: z.iso.datetime().nullable(),
  revoked_reason: z.string().nullable(),
});
export type AdminSession = z.infer<typeof AdminSession>;

// ---------------------------------------------------------------------------
// API clients + keys
// ---------------------------------------------------------------------------

export const AdminApiClient = z.object({
  email_policy_required:z.boolean().optional(),
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  owner_user_id: z.uuid().nullable(),
  default_ticket_service_id: z.uuid().nullable(),
  status: z.enum(["active", "archived"]),
  created_at: z.iso.datetime(),
});
export type AdminApiClient = z.infer<typeof AdminApiClient>;

export const AdminApiClientInput = z.strictObject({
  email_policy_required:z.boolean().optional(),
  code: z.string().trim().regex(/^[A-Z0-9_]{2,32}$/),
  name: z.string().trim().min(1).max(160),
  owner_user_id: z.uuid().nullable().optional(),
  default_ticket_service_id: z.uuid().nullable().optional(),
});
export type AdminApiClientInput = z.infer<typeof AdminApiClientInput>;
export const AdminApiClientPatch = AdminApiClientInput.partial().extend({ status: z.enum(["active", "archived"]).optional() });
export type AdminApiClientPatch = z.infer<typeof AdminApiClientPatch>;

/** [T-073 · #212] service ที่ map กับ api client — ขอบเขต visibility ของ change feed/webhook (ไม่ใช่ค่าเริ่มต้นตอนสร้างการ์ด) */
export const AdminApiClientService = z.object({
  id: z.uuid(),
  code: z.string(),
  name: I18n,
  status: z.enum(["active", "archived"]),
  created_at: z.iso.datetime(),
});
export type AdminApiClientService = z.infer<typeof AdminApiClientService>;

export const AdminApiClientServicesInput = z.strictObject({
  /** ชุดใหม่ทั้งหมด (แทนที่) · ว่าง = ล้าง mapping */
  service_ids: z.array(z.uuid()).max(200),
});
export type AdminApiClientServicesInput = z.infer<typeof AdminApiClientServicesInput>;

/** Per-key message visibility: exact sender AND stream; omission preserves legacy client-wide access. */
export const EmailReadPolicy = z.strictObject({
  allowed_from: z.array(z.email().max(254)).max(100),
  allowed_streams: z.array(z.string().min(1).max(64)).max(100),
});
export type EmailReadPolicy = z.infer<typeof EmailReadPolicy>;

const EmailPolicy = z.object({
  read_policy: EmailReadPolicy.optional(),
  allowed_from: z.array(z.string()),
  allowed_streams: z.array(z.string()),
  max_priority: z.string().nullable(),
  rate_limit_rps: z.number(),
  sandbox: z.boolean(),
});

export const AdminApiKey = z.object({
  id: z.uuid(),
  client_id: z.uuid(),
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(z.string()),
  act_as_policy: z.enum(["none", "any", "created_by_service", "groups"]),
  act_as_group_ids: z.array(z.uuid()),
  ip_allowlist: z.array(z.string()),
  email_policy: EmailPolicy.nullable(),
  expires_at: z.iso.datetime().nullable(),
  last_used_at: z.iso.datetime().nullable(),
  rotated_from_id: z.uuid().nullable(),
  revoked_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
});
export type AdminApiKey = z.infer<typeof AdminApiKey>;

export const AdminApiKeyInput = z.strictObject({
  client_id: z.uuid(),
  name: z.string().trim().min(1).max(160),
  scopes: z.array(z.string()).min(1).max(20),
  act_as_policy: z.enum(["none", "any", "created_by_service", "groups"]).optional(),
  act_as_group_ids: z.array(z.uuid()).max(50).optional(),
  ip_allowlist: z.array(z.string()).max(50).optional(),
  email_policy: EmailPolicy.optional(),
  expires_at: z.iso.datetime().nullable().optional(),
});
export type AdminApiKeyInput = z.infer<typeof AdminApiKeyInput>;

export const AdminApiKeyCreated = AdminApiKey.extend({ key: z.string() });
export type AdminApiKeyCreated = z.infer<typeof AdminApiKeyCreated>;

export const AdminApiRequest = z.object({
  id: z.number().int(),
  at: z.iso.datetime(),
  api_key_id: z.uuid().nullable(),
  client_id: z.uuid().nullable(),
  act_as_user_id: z.uuid().nullable(),
  method: z.string(),
  path: z.string(),
  status: z.number().int(),
  latency_ms: z.number().int(),
  ip: z.string().nullable(),
  request_id: z.string().nullable(),
  error_code: z.string().nullable(),
});
export type AdminApiRequest = z.infer<typeof AdminApiRequest>;

// ---------------------------------------------------------------------------
// Field definitions + tags
// ---------------------------------------------------------------------------

export const FieldDefinition = z.object({
  id: z.uuid(),
  key: z.string(),
  label: I18n,
  type: z.string(),
  options: z.array(z.unknown()).nullable(),
  validation: z.record(z.string(), z.unknown()).nullable(),
  scope_type: z.enum(["global", "service", "team"]),
  scope_id: z.uuid().nullable(),
  customer_visible: z.boolean(),
  customer_editable: z.boolean(),
  show_in_list: z.boolean(),
  searchable: z.boolean(),
  sensitive: z.boolean(),
  default_value: z.unknown().nullable(),
  ai_extractable: z.boolean(),
  visible_if: z.record(z.string(), z.unknown()).nullable(),
  sort_order: z.number().int(),
  archived_at: z.iso.datetime().nullable(),
});
export type FieldDefinition = z.infer<typeof FieldDefinition>;

export const FIELD_TYPES = ["text", "textarea", "number", "select", "multiselect", "checkbox", "date", "datetime", "email", "url", "phone"] as const;

export const FieldDefinitionInput = z.strictObject({
  key: z.string().trim().min(1).max(64).regex(/^[a-z0-9_]+$/),
  label: I18n,
  type: z.enum(FIELD_TYPES),
  options: z.array(z.unknown()).optional(),
  validation: z.record(z.string(), z.unknown()).optional(),
  scope_type: z.enum(["global", "service", "team"]).optional(),
  scope_id: z.uuid().nullable().optional(),
  customer_visible: z.boolean().optional(),
  customer_editable: z.boolean().optional(),
  show_in_list: z.boolean().optional(),
  searchable: z.boolean().optional(),
  sensitive: z.boolean().optional(),
  default_value: z.unknown().optional(),
  ai_extractable: z.boolean().optional(),
  visible_if: z.record(z.string(), z.unknown()).optional(),
  sort_order: z.number().int().optional(),
});
export type FieldDefinitionInput = z.infer<typeof FieldDefinitionInput>;
export const FieldDefinitionPatch = FieldDefinitionInput.partial().extend({
  /** [#63] null = unarchive · ISO = archive */
  archived_at: z.iso.datetime().nullable().optional(),
});
export type FieldDefinitionPatch = z.infer<typeof FieldDefinitionPatch>;

export const AdminTag = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  color: z.string(),
  description: z.string().nullable(),
  scope_type: z.enum(["global", "service", "team"]),
  scope_id: z.uuid().nullable(),
  archived_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
});
export type AdminTag = z.infer<typeof AdminTag>;

export const AdminTagInput = z.strictObject({
  name: z.string().trim().min(1).max(64),
  color: z.string().trim().min(1).max(32).optional(),
  description: z.string().max(500).nullable().optional(),
});
export type AdminTagInput = z.infer<typeof AdminTagInput>;
export const AdminTagPatch = AdminTagInput.partial();
export type AdminTagPatch = z.infer<typeof AdminTagPatch>;

// ---------------------------------------------------------------------------
// Business hours + holiday calendars
// ---------------------------------------------------------------------------

export const BusinessHours = z.object({
  id: z.uuid(),
  name: z.string(),
  timezone: z.string(),
  weekly: z.record(z.string(), z.array(z.tuple([z.string(), z.string()]))),
  holiday_calendar_id: z.uuid().nullable(),
  extra_holidays: z.array(z.string()),
});
export type BusinessHours = z.infer<typeof BusinessHours>;

export const BusinessHoursInput = z.strictObject({
  name: z.string().trim().min(1).max(160),
  timezone: z.string().min(1).max(64).optional(),
  weekly: z.record(z.string(), z.array(z.tuple([z.string(), z.string()]))),
  holiday_calendar_id: z.uuid().nullable().optional(),
  extra_holidays: z.array(z.iso.date()).optional(),
});
export type BusinessHoursInput = z.infer<typeof BusinessHoursInput>;
export const BusinessHoursPatch = BusinessHoursInput.partial();
export type BusinessHoursPatch = z.infer<typeof BusinessHoursPatch>;

export const HolidayCalendar = z.object({
  id: z.uuid(),
  name: z.string(),
  dates: z.record(z.string(), z.array(z.string())),
});
export type HolidayCalendar = z.infer<typeof HolidayCalendar>;

export const HolidayCalendarInput = z.strictObject({
  name: z.string().trim().min(1).max(160),
  dates: z.record(z.string(), z.array(z.iso.date())).optional(),
});
export type HolidayCalendarInput = z.infer<typeof HolidayCalendarInput>;
export const HolidayCalendarPatch = HolidayCalendarInput.partial();
export type HolidayCalendarPatch = z.infer<typeof HolidayCalendarPatch>;

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export const Template = z.object({
  id: z.uuid(),
  kind: z.enum(["canned", "system", "signature", "auto_reply"]),
  key: z.string().nullable(),
  scope_type: z.enum(["global", "service", "team", "user"]),
  scope_id: z.uuid().nullable(),
  name: I18n,
  subject: I18n.nullable(),
  body_html: I18n,
  body_text: I18n.nullable(),
  variables: z.array(z.string()),
  created_by: z.uuid().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  archived_at: z.iso.datetime().nullable(),
});
export type Template = z.infer<typeof Template>;

export const TemplateInput = z.strictObject({
  kind: z.enum(["canned", "signature", "auto_reply"]).optional(),
  scope_type: z.enum(["global", "service", "team", "user"]).optional(),
  scope_id: z.uuid().nullable().optional(),
  name: I18n,
  subject: I18n.optional(),
  body_html: I18n,
  body_text: I18n.optional(),
  variables: z.array(z.string()).max(50).optional(),
});
export type TemplateInput = z.infer<typeof TemplateInput>;

export const TemplatePatch = z.strictObject({
  scope_type: z.enum(["global", "service", "team", "user"]).optional(),
  scope_id: z.uuid().nullable().optional(),
  name: I18n.optional(),
  subject: I18n.optional(),
  body_html: I18n.optional(),
  body_text: I18n.optional(),
  variables: z.array(z.string()).max(50).optional(),
});
export type TemplatePatch = z.infer<typeof TemplatePatch>;

export const TemplateTestSend = z.strictObject({ to: z.email(), lang: z.string().min(2).max(8).optional() });
export type TemplateTestSend = z.infer<typeof TemplateTestSend>;

// ---------------------------------------------------------------------------
// Settings (discriminated by key)
// ---------------------------------------------------------------------------

export const SettingsSecurity = z.strictObject({
  ip_allowlist_by_role: z.record(z.string(), z.array(z.string())),
  reauth_max_age_sec: z.number().int().min(60).max(86400),
  session_idle_min: z.number().int().positive().optional(),
  session_absolute_hours: z.number().int().positive().optional(),
});
export type SettingsSecurity = z.infer<typeof SettingsSecurity>;

export const SettingsNotificationDefaults = z.record(z.string(), z.record(z.string(), z.array(z.string())));
export type SettingsNotificationDefaults = z.infer<typeof SettingsNotificationDefaults>;

export const SettingsCustomization = z.strictObject({
  title: z.string().trim().min(1).max(120),
  logo_url: z.string().max(2048).nullable(),
  footer_html: z.string().max(20_000),
});
export type SettingsCustomization = z.infer<typeof SettingsCustomization>;

/** [Lead #79] ค่าที่หน้า portal (ไม่ต้องล็อกอิน) ต้องรู้ — อ่านผ่าน GET /api/v1/settings/public */
export const SettingsPortal = z.strictObject({
  /** slug ของฟอร์มที่ปุ่ม "ส่งเรื่องใหม่" ชี้ไป */
  default_form_slug: z.string().trim().min(1).max(120).optional(),
  /** ลิงก์นโยบายความเป็นส่วนตัว (ซ่อนลิงก์เมื่อไม่ตั้ง) */
  privacy_url: z.string().max(2048).nullable().optional(),
  /** อีเมลติดต่อในฟุตเตอร์ (ซ่อนเมื่อไม่ตั้ง) */
  contact_email: z.email().nullable().optional(),
  /** ประโยค SLA ใต้หัวข้อหน้าแรก เช่น "ตอบกลับภายใน 1 วันทำการ" (ไม่ตั้ง = ใช้ข้อความทั่วไป) */
  reply_sla_th: z.string().max(200).nullable().optional(),
  reply_sla_en: z.string().max(200).nullable().optional(),
});
export type SettingsPortal = z.infer<typeof SettingsPortal>;

// [M4-14 · Lead-authorized re-review] รายชื่อ DPO ที่อนุมัติ eDiscovery (R6 · D-041 M4-3) —
// จัดการผ่าน PUT /api/v1/admin/settings/mail · ว่าง/ไม่มีแถว = eDiscovery approve ใช้ไม่ได้ (fail-closed)
export const SettingsMail = z.strictObject({
  dpo_user_ids: z.array(z.uuid()).max(20).default([]),
});
export type SettingsMail = z.infer<typeof SettingsMail>;

/** [Lead #79] ผลลัพธ์ของ GET /api/v1/settings/public — เปิดเผยได้ ไม่ต้องมี session */
export const PublicSettings = z.object({
  title: z.string(),
  logo_url: z.string().nullable(),
  portal: SettingsPortal,
});
export type PublicSettings = z.infer<typeof PublicSettings>;

export const SettingsFeatureFlags = z.record(z.string(), z.union([z.boolean(), z.number()]));
export type SettingsFeatureFlags = z.infer<typeof SettingsFeatureFlags>;

export const SettingsFiles = z.strictObject({
  max_file_mb: z.number().positive().max(1024),
  max_ticket_mb: z.number().positive().max(4096),
});
export type SettingsFiles = z.infer<typeof SettingsFiles>;

// [T-054] integrations — ค่า secret (telegram_bot_token / telegram_webhook_secret) เก็บ **เข้ารหัส** `enc:…` เท่านั้น
// (ตั้งค่าผ่าน PUT /api/v1/admin/integrations/telegram ที่เข้ารหัสให้ — ห้ามส่ง plain เข้า generic settings PUT)
export const SettingsIntegrations = z.strictObject({
  telegram_bot_username: z.string().max(64).nullable().optional(),
  telegram_bot_token: z
    .string()
    .max(500)
    .refine((v) => v.startsWith("enc:"), { message: "ต้องเป็นค่าเข้ารหัส enc:… — ตั้งค่าผ่าน /api/v1/admin/integrations/telegram" })
    .nullable()
    .optional(),
  telegram_webhook_secret: z
    .string()
    .max(500)
    .refine((v) => v.startsWith("enc:"), { message: "ต้องเป็นค่าเข้ารหัส enc:… — ตั้งค่าผ่าน /api/v1/admin/integrations/telegram" })
    .nullable()
    .optional(),
});
export type SettingsIntegrations = z.infer<typeof SettingsIntegrations>;

// [T-054] ai — T-052 อ่านผ่าน readSetting(db, "ai") (W4-CONTRACTS §3) · ไม่มี = default
export const SettingsAi = z.strictObject({
  budget_usd_month: z.number().nonnegative().max(10_000).optional(),
  features: z
    .record(
      z.string(),
      z.strictObject({
        model: z.string().max(120).optional(),
        threshold: z.number().min(0).max(1).optional(),
        budget_usd_month: z.number().nonnegative().max(10_000).optional(),
        enabled: z.boolean().optional(),
      }),
    )
    .optional(),
});
export type SettingsAi = z.infer<typeof SettingsAi>;

export const SETTINGS_SCHEMAS = {
  security: SettingsSecurity,
  notification_defaults: SettingsNotificationDefaults,
  customization: SettingsCustomization,
  feature_flags: SettingsFeatureFlags,
  files: SettingsFiles,
  integrations: SettingsIntegrations, // [T-054]
  ai: SettingsAi, // [T-054]
  portal: SettingsPortal, // [Lead #79]
  mail: SettingsMail, // [M4-14 · Lead-authorized re-review] DPO approvers ของ eDiscovery (R6 · D-041 M4-3)
} as const;
export type SettingsKey = keyof typeof SETTINGS_SCHEMAS;
export const SETTINGS_KEYS = Object.keys(SETTINGS_SCHEMAS) as SettingsKey[];

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export const AuditEntry = z.object({
  id: z.number().int(),
  at: z.iso.datetime(),
  actor_user_id: z.uuid().nullable(),
  actor_service_id: z.uuid().nullable(),
  actor_api_key_id: z.uuid().nullable(),
  act_as_user_id: z.uuid().nullable(),
  action: z.string(),
  target_type: z.string().nullable(),
  target_id: z.string().nullable(),
  ticket_id: z.uuid().nullable(),
  ip: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
});
export type AuditEntry = z.infer<typeof AuditEntry>;

export const AuditListQuery = z.object({
  actor: z.uuid().optional(),
  action: z.string().max(100).optional(),
  target: z.string().max(100).optional(),
  since: z.iso.datetime().optional(),
  until: z.iso.datetime().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AuditListQuery = z.infer<typeof AuditListQuery>;

export const AuditList = z.object({ items: z.array(AuditEntry), next_cursor: z.string().nullable() });
export type AuditList = z.infer<typeof AuditList>;

// ---------------------------------------------------------------------------
// Users search (staff) — R4
// ---------------------------------------------------------------------------

export const UserSearchResult = z.object({
  id: z.uuid(),
  display_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
  kind: z.enum(["staff", "customer", "guest", "service"]),
  primary_email: z.string(),
});
export type UserSearchResult = z.infer<typeof UserSearchResult>;

export const UserSearchQuery = z.object({
  q: z.string().trim().min(1).max(200),
  limit: z.coerce.number().int().min(1).max(20).default(20),
});
export type UserSearchQuery = z.infer<typeof UserSearchQuery>;
